import {Component, OnInit} from '@angular/core';
import {DashboardTitleService} from '../../../services/dashboard-title.service';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {CardModule} from 'primeng/card';
import {ScrollPanelModule} from 'primeng/scrollpanel';
import {TranslationPipe} from '../../../pipes/translation.pipe';
import {DateTime} from 'luxon';
import {ButtonModule} from 'primeng/button';
import {RippleModule} from 'primeng/ripple';
import {CommonModule} from '@angular/common';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faDog} from '@fortawesome/free-solid-svg-icons/faDog';
import {
  CustomerClient,
  CustomerDto, DocumentClient, DocumentShortInfoDto, Gender,
  PetClient,
  PetDto,
  TreatmentClient,
  TreatmentShortInfoDto
} from '../../../api/api-client';
import {FeedbackDataService} from '../../../services/feedback-data.service';
import {FeedbackData} from '../../../models/feedback-data';
import {SafeHtmlPipe} from '../../../pipes/safe-html.pipe';
import {SkeletonModule} from 'primeng/skeleton';
import {AddDocumentDialogComponent} from '../../dialogs/add-document-dialog/add-document-dialog.component';
import {ViewDocumentDialogComponent} from '../../dialogs/view-document-dialog/view-document-dialog.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DeleteDocumentDialogComponent} from '../../dialogs/delete-document-dialog/delete-document-dialog.component';
import {CardWithHeaderComponent} from '../../widgets/card-with-header/card-with-header.component';
import {GreedyScrollPanelComponent} from '../../widgets/greedy-scroll-panel/greedy-scroll-panel.component';
import {AttachmentGalleryComponent} from '../../widgets/attachment-gallery/attachment-gallery.component';
import {SubscriptionManager} from '../../../services/subscription-manager.service';
import { ViewDocumentRoutingInfo } from '../../../models/view-document-routing-info';

@Component({
  selector: 'app-customer-detail',
  imports: [
    RouterModule,
    CommonModule,
    CardModule,
    ScrollPanelModule,
    TranslationPipe,
    ButtonModule,
    RippleModule,
    FontAwesomeModule,
    SafeHtmlPipe,
    SkeletonModule,
    CardWithHeaderComponent,
    GreedyScrollPanelComponent,
    AttachmentGalleryComponent
  ],
  providers: [TranslationPipe, SubscriptionManager],
  templateUrl: './customer-detail.component.html',
  styleUrl: './customer-detail.component.scss'
})
export class CustomerDetailComponent implements OnInit {
  customerId!: string;
  customer!: CustomerDto;
  nextTreatments!: TreatmentShortInfoDto[];
  pets !: PetDto[];
  shouldShowBehavior = false;
  allTreatments!: TreatmentShortInfoDto[];
  pinnedDocuments: DocumentShortInfoDto[] = [];
  notPinnedDocuments: DocumentShortInfoDto[] = [];
  refAddDocumentDialog!: DynamicDialogRef<AddDocumentDialogComponent> | null;
  refViewDocumentDialog!: DynamicDialogRef<ViewDocumentDialogComponent> | null;
  refDeleteDocumentDialog!: DynamicDialogRef<DeleteDocumentDialogComponent> | null;

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly dashboardService: DashboardTitleService,
    private readonly customerService: CustomerClient,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly treatmentService: TreatmentClient,
    private readonly petService: PetClient,
    private readonly translationPipe: TranslationPipe,
    private readonly feedbackService: FeedbackDataService,
    private readonly documentService: DocumentClient,
    private readonly dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptionManager.call(
      this.route.paramMap,
      (paramMap => {
        this.customerId = paramMap.get('customerId') ?? "";
      })
    );
    this.feedbackService.feedbackData = new FeedbackData();
    this.feedbackService.feedbackData.customerId = this.customerId;
    this.subscriptionManager.call(
      this.customerService.getCustomerById(this.customerId),
      (x => {
        this.customer = x.result;
        this.dashboardService.customerFirstname = this.customer.firstName;
        this.dashboardService.customerLastname = this.customer.lastName;
      })
    );
    this.subscriptionManager.call(
      this.petService.getAllPetsForCustomer(this.customerId),
      (x => {
        this.pets = x.result;
      })
    );
    this.subscriptionManager.call(
      this.treatmentService.getTreatmentsForCustomer(this.customerId),
      (x => {
        this.allTreatments = x.result;
        this.nextTreatments = x.result.filter(y => y.appointmentDate.diffNow().as('minutes') > 0).sort((a, b) => a.appointmentDate.diffNow().as('minutes') - b.appointmentDate.diffNow().as('minutes')).slice(0, 2)
      })
    );
    this.subscriptionManager.call(
      this.documentService.getDocumentShortInfosForCustomer(this.customerId),
      (x => {
        this.pinnedDocuments = x.result.filter(y => y.isPinned);
        this.notPinnedDocuments = x.result.filter(y => !y.isPinned);
      })
    );
  }

  getDate(date: DateTime) {
    return date.toLocaleString({day: '2-digit', month: '2-digit', year: 'numeric'});
  }

  getTime(date: DateTime) {
    return date.toLocaleString(DateTime.TIME_24_SIMPLE)
  }

  toggleShouldShowBehavior(): void {
    this.shouldShowBehavior = !this.shouldShowBehavior;
  }

  getGender(pet: PetDto): string {
    switch (pet.gender) {
      case 'Male':
        return this.translationPipe.transform("Pet.Gender.Male")
      case 'Female':
        return this.translationPipe.transform("Pet.Gender.Female")
      case 'Unknown':
        return this.translationPipe.transform("Pet.Gender.Unknown")
      default:
        return this.translationPipe.transform("Pet.Gender.Unknown")
    }
  }

  getNeuteredStatus(pet: PetDto): string {
    switch (pet.neuteringStatus) {
      case "Unknown":
        return this.translationPipe.transform("Pet.NeuteringStatus.UnknownLong")
      case "Neutered":
        return this.translationPipe.transform("Pet.NeuteringStatus.NeuteredLong")
      case "NotNeutered":
        return this.translationPipe.transform("Pet.NeuteringStatus.NotNeuteredLong")
      default:
        return this.translationPipe.transform("Pet.NeuteringStatus.UnknownLong")
    }
  }

  getFirstTreatmentForPet(pet: PetDto) {
        return pet.firstTreatmentDate.toLocaleString({
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
  }

  getLastTreatmentForPet(pet: PetDto) {
    if (this.allTreatments) {
      let treatmentList = this.allTreatments.filter(x => x.patientName == pet.nickname && x.appointmentDate < DateTime.now()).sort((a, b) => b.appointmentDate.toMillis() - a.appointmentDate.toMillis()).slice(0, 1);
      if (treatmentList.length > 0) {
        return treatmentList[0].appointmentDate.toLocaleString({
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      } else {
        return this.translationPipe.transform("CustomerDetail.Errors.NoTreatment");
      }
    }
    return DateTime.now()
  }

  estimateHighDangerClassActive(pet: PetDto): string {
    if (pet.behavior == "HighDanger") {
      return "border-2 border-(--p-text-color)"
    }
    return "saturate-[30%]";
  }

  estimateMediumDangerClassActive(pet: PetDto): string {
    if (pet.behavior == "MediumDanger") {
      return "border-2 border-(--p-text-color)"
    }
    return "saturate-[30%]";
  }

  estimateLowDangerClassActive(pet: PetDto): string {
    if (pet.behavior == "LowDanger") {
      return "border-2 border-(--p-text-color)"
    }
    return "saturate-[30%]";
  }

  estimatePerfectlySafeClassActive(pet: PetDto): string {
    if (pet.behavior == "PerfectlySafe") {
      return "border-2 border-(--p-text-color)"
    }
    return "saturate-[30%]";
  }

  protected readonly faDog = faDog;

  protected openEditCustomerPage() {
    this.router.navigate(['../loggedin/customers/edit/' + this.customerId]);
  }

  openAddDocumentDialog(docType: string, isSkeleton = false) {
    this.refAddDocumentDialog = this.dialogService.open(AddDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        sourcePage: 'customer-detail',
        documentType: docType,
        entityId: this.customerId,
        isSkeleton: isSkeleton,
      }
    });
    this.subscriptionManager.call(
      this.refAddDocumentDialog?.onClose,
      (x => {
        this.ngOnInit();
      })
    );
  }

  openViewDocumentDialog(documentId: string) {
    this.subscriptionManager.call(
      this.documentService.getDocumentById(documentId),
      (x => {
        this.refViewDocumentDialog = this.dialogService.open(ViewDocumentDialogComponent, {
          modal: true,
          closeOnEscape: true,
          closable: true,
          header: x.result.filename,
          data: {
            document: x.result
          } as ViewDocumentRoutingInfo
        });
      })
    );
  }

  unpinDocument(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.unpinDocument(document.id),
      (x => {
        let i = this.pinnedDocuments.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.pinnedDocuments[i].isPinned = false;
          this.notPinnedDocuments.push(this.pinnedDocuments[i]);
          this.pinnedDocuments.splice(i, 1);
        }
      })
    );
  }

  pinDocument(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.pinDocument(document.id),
      (x => {
        let i = this.notPinnedDocuments.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.notPinnedDocuments[i].isPinned = true;
          this.pinnedDocuments.push(this.notPinnedDocuments[i]);
          this.notPinnedDocuments.splice(i, 1);
        }
      })
    );
  }

  protected openDeleteDocumentDialog(document: DocumentShortInfoDto) {
    this.refDeleteDocumentDialog = this.dialogService.open(DeleteDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        entityId: document.id,
        filename: document.filename,
        documentType: document.documentType
      }
    });
    this.subscriptionManager.call(
      this.refDeleteDocumentDialog?.onClose,
      (x => {
        if (x) {
          this.pinnedDocuments = this.pinnedDocuments.filter(x => x.id != document.id);
          this.notPinnedDocuments = this.notPinnedDocuments.filter(x => x.id != document.id);
        }
      })
    );
  }
}
